/**
 * The preview's playhead (Dax, 2026-10-04): one slider across the cut, for long cuts as much as short ones.
 * Dragging it shows the frame under it and leaves the playhead there, so play starts from that moment;
 * dragged while playing, playback carries on from where it is let go. It follows playback by reading the
 * position every frame itself, so only the slider redraws, never the page around it.
 */
import { useEffect, useRef, useState } from 'react';
import { fmtTime } from '@/lib/format';

export function PreviewScrubber({ totalMs, position, onSeek }: {
  totalMs: number;
  /** Where the cut is now, in ms from its first frame. */
  position: () => number;
  /** The playhead moved by hand to `ms`; `done` when it is let go. */
  onSeek: (ms: number, done: boolean) => void;
}) {
  const [ms, setMs] = useState(0);
  const dragging = useRef(false);
  const read = useRef(position);
  read.current = position;
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      if (!dragging.current) setMs(Math.max(0, Math.min(totalMs, Math.round(read.current()))));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [totalMs]);
  const release = () => {
    if (!dragging.current) return;
    dragging.current = false;
    onSeek(ms, true);
  };
  return (
    <div className="row pv-scrub">
      <input type="range" className="pv-scrub-range" min={0} max={Math.max(1, totalMs)} step={1} value={ms} aria-label="Playhead"
        aria-valuetext={fmtTime(ms)} disabled={totalMs <= 0}
        onPointerDown={() => { dragging.current = true; }}
        onChange={(e) => {
          const next = Number(e.target.value);
          setMs(next);
          onSeek(next, !dragging.current);
        }}
        onPointerUp={release}
        onPointerCancel={release} />
      <span className="sub mono pv-scrub-time">{fmtTime(ms)}</span>
    </div>
  );
}
